import styled from '@emotion/styled';
import { MEDIA_QUERIES } from 'radiance-ui/lib/constants';
import { useMemo } from 'react';

import CONFIG from 'consts/config';
import { PRODUCT_LINKS } from 'consts/products';
import ecommerceProducts from 'data/products.json';
import { SubsectionProps } from './components';
import {
  ECOM_PRODUCTS_MODULE,
  RX_PRODUCTS_MODULE,
  CHIA_PRODUCTS_MODULE,
  CHIA_BOTTOM_PRODUCTS_MODULE,
} from './content';
import maskImage from './images/product_mask.png';
import { ResponsiveProductImage } from 'components/global/ResponsiveProductImage';

type ProductsContent = [SubsectionProps, SubsectionProps];

export interface EcommerceProduct {
  name: string;
  description: string;
  price: number;
  shopPath: string;
  imageUrl: string;
  order: number;
}

const ECOM_PRODUCT_ORDER_OVERRIDE = [
  'Starter Set',
  'The Cleanser',
  'Acne Cleanser',
  'The Moisturizer',
  'The Rich Moisturizer',
  'Emergency Spot Patch',
  'The Lip Balm',
  'The Sunscreen',
  'Acne Body Wash',
  'Micellar Makeup Remover',
];

const EcomProductImageContainer = styled.div`
  /* remove 'Dermatologist' banner from Ecom product images with a mask */
  mask-image: url(${maskImage});
  mask-size: contain;
  mask-mode: alpha;
  mask-repeat: no-repeat;

  /* trim sides of wider Ecom product images and center result */
  display: flex;
  justify-content: center;
  align-items: center;
  width: 240px;
  ${MEDIA_QUERIES.mdUp} {
    width: 218px;
  }
`;

const getProductOrder = (product: EcommerceProduct): number => {
  const overrideOrder = ECOM_PRODUCT_ORDER_OVERRIDE.indexOf(product.name);
  return overrideOrder !== -1 ? overrideOrder : product.order;
};

export const useProductsContent = (showChia?: boolean): ProductsContent => {
  const productCards = useMemo(
    () =>
      ecommerceProducts
        .map((product) => ({
          ...product,
          order: getProductOrder(product),
        }))
        .sort((current, next) => current.order - next.order)
        .map(({ name, description, price, shopPath, imageUrl }) => ({
          linkType: PRODUCT_LINKS.eCommerce,
          href: CONFIG.ECOMMERCE_URL + shopPath,
          name,
          description: description.includes(' \u2022 ')
            ? description.split(' \u2022 ')[1]
            : description,
          price: price.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          }),
          image: (
            <EcomProductImageContainer>
              <ResponsiveProductImage
                src={imageUrl}
                alt={`${name} by Curology`}
                displayWidth={{ mobile: 240, desktop: 218 }}
              />
            </EcomProductImageContainer>
          ),
          hideButton: showChia,
        })),
    [ecommerceProducts],
  );

  return showChia
    ? [
        {
          ...CHIA_PRODUCTS_MODULE,
        },
        {
          ...CHIA_BOTTOM_PRODUCTS_MODULE,
          productCards,
        },
      ]
    : [
        {
          ...RX_PRODUCTS_MODULE,
        },
        {
          ...ECOM_PRODUCTS_MODULE,
          productCards,
        },
      ];
};
